import { Text, View } from "react-native";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFocusEffect, usePathname, useRouter } from "expo-router";
import {
  incomeTypeRequiresPerson,
  isSocialSecurityIncomeType,
  type IncomeType,
} from "@perdiem/trpc/income-types";
import { trpc } from "../../lib/trpc";
import { trackOnboardingStepCompleted } from "../../lib/product-events";
import {
  OnboardingShell,
  Button,
  Card,
  FormErrorBanner,
  LoadingSpinner,
  IncomeStreamForm,
  IncomeStreamList,
  SectionHeader,
  SocialSecurityEstimator,
  buildSocialSecurityEstimatePeople,
  WebHead,
} from "../../components";

export default function Step4Income() {
  const router = useRouter();
  const pathname = usePathname();
  const utils = trpc.useUtils();
  const advanceMutation = trpc.households.updateOnboardingStep.useMutation();

  const incomeQ = trpc.income.list.useQuery();
  const profilesQ = trpc.profiles.list.useQuery();
  const connectionsQ = trpc.connections.list.useQuery();
  const createDefaultsMutation = trpc.income.createDefaults.useMutation();
  const createMutation = trpc.income.create.useMutation();
  const createSocialSecurityMutation =
    trpc.income.createSocialSecurityEstimates.useMutation();

  const [error, setError] = useState<string | null>(null);
  const [autoDetectMessage, setAutoDetectMessage] = useState<string | null>(
    null,
  );
  const [autoDetectAttempted, setAutoDetectAttempted] = useState(false);
  const autoDetectAttemptedRef = useRef(false);
  const [socialSecurityOptedOut, setSocialSecurityOptedOut] = useState(false);

  const incomeStreams = incomeQ.data ?? [];
  const profiles = profilesQ.data ?? [];
  const unassignedWageCount = incomeStreams.filter(
    (stream) => incomeTypeRequiresPerson(stream.type) && !stream.personId,
  ).length;
  const hasUnassignedWages = unassignedWageCount > 0;
  const totalAnnualIncome = incomeStreams.reduce(
    (sum, stream) => sum + Number(stream.annualAmount ?? 0),
    0,
  );
  const hasSocialSecurityIncome = incomeStreams.some((s) =>
    isSocialSecurityIncomeType(s.type),
  );
  const isResolvingBackRoute = connectionsQ.isLoading && !connectionsQ.data;
  const hasTransactionReviewSource = (connectionsQ.data ?? []).some(
    (connection) => connection.provider === "plaid" || connection.provider === "teller",
  );
  const skippedTransactionReview = !isResolvingBackRoute && !hasTransactionReviewSource;

  useFocusEffect(
    useCallback(() => {
      void profilesQ.refetch();
    }, [profilesQ.refetch]),
  );

  const runAutoDetectIncome = useCallback(async () => {
    setError(null);
    setAutoDetectMessage(null);
    try {
      const result = await createDefaultsMutation.mutateAsync();
      await Promise.all([
        incomeQ.refetch(),
        utils.fire.getRecalcStatus.invalidate(),
      ]);
      if (result.created > 0) {
        setAutoDetectMessage(
          `Added ${result.created} income stream${result.created === 1 ? "" : "s"} from deposits.`,
        );
      } else {
        setAutoDetectMessage(
          "No qualifying income streams were detected. You can add one manually below.",
        );
      }
    } catch {
      setError(
        "Could not auto-detect income from deposits. You can add income manually below.",
      );
    }
  }, [createDefaultsMutation, incomeQ, utils.fire.getRecalcStatus]);

  useEffect(() => {
    if (autoDetectAttemptedRef.current) return;
    if (incomeQ.isLoading || connectionsQ.isLoading) return;
    if (!hasTransactionReviewSource || incomeStreams.length > 0) return;

    autoDetectAttemptedRef.current = true;
    setAutoDetectAttempted(true);
    void runAutoDetectIncome();
  }, [
    connectionsQ.isLoading,
    hasTransactionReviewSource,
    incomeQ.isLoading,
    incomeStreams.length,
    runAutoDetectIncome,
  ]);

  const handleAddManual = async (input: {
    name: string;
    monthlyAmount: number;
    type: IncomeType;
    personId: string | null;
    growthRate: number;
    begin: string | null;
    end: string | null;
    deductions?: Record<string, number>;
    payFrequency?: string;
  }) => {
    setError(null);
    setAutoDetectMessage(null);
    try {
      await createMutation.mutateAsync({
        name: input.name,
        type: input.type,
        amount: input.monthlyAmount,
        growthRate: input.growthRate,
        ...(input.personId ? { personId: input.personId } : {}),
        ...(input.begin ? { begin: input.begin } : {}),
        ...(input.end ? { end: input.end } : { end: null }),
        ...(input.deductions ?? {}),
        ...(input.payFrequency ? { payFrequency: input.payFrequency as "weekly" | "biweekly" | "semimonthly" | "monthly" } : {}),
      });
      await Promise.all([
        incomeQ.refetch(),
        utils.fire.getRecalcStatus.invalidate(),
      ]);
    } catch (e) {
      setError("Could not save income stream. Please try again.");
      throw e;
    }
  };

  const handleContinue = async () => {
    let socialSecurityEstimateCount = 0;
    if (!hasSocialSecurityIncome && !socialSecurityOptedOut) {
      const people = buildSocialSecurityEstimatePeople({
        profiles,
        incomeStreams,
      });
      if (people.length > 0) {
        try {
          const result = await createSocialSecurityMutation.mutateAsync({
            people,
          });
          socialSecurityEstimateCount = result?.estimates.length ?? 0;
          await Promise.all([
            incomeQ.refetch(),
            utils.fire.getRecalcStatus.invalidate(),
          ]);
        } catch {
          setError(
            "Could not add Social Security estimates. Remove the estimate or try again.",
          );
          return;
        }
      }
    }

    await advanceMutation.mutateAsync({ step: 5 });
    trackOnboardingStepCompleted({
      route: pathname,
      stepNumber: 4,
      nextStepNumber: 5,
      completionKind: "continue",
      metadata: {
        incomeStreamCount: incomeStreams.length,
        totalAnnualIncome: Math.round(totalAnnualIncome),
        socialSecurityEstimateCount,
        socialSecurityOptedOut,
      },
    });
    router.replace("/(onboarding)/step-5-budget" as any);
  };

  const handleIncomeMutated = async () => {
    await Promise.all([
      incomeQ.refetch(),
      utils.fire.getRecalcStatus.invalidate(),
    ]);
  };

  return (
    <OnboardingShell
      step={3}
      title="Verify Income"
      subtitle="Confirm your income sources. Per Diem uses these to calculate your savings rate and FIRE timeline."
      backDisabled={isResolvingBackRoute}
      {...(skippedTransactionReview
        ? {
            backRoute: "/(onboarding)/step-2-connect",
            backStoredStep: 2,
          }
        : {})}
      footer={
        <Button
          testID="onboarding-income-continue-button"
          accessibilityLabel="Continue from income review"
          onPress={handleContinue}
          loading={
            advanceMutation.isPending ||
            createDefaultsMutation.isPending ||
            createSocialSecurityMutation.isPending
          }
          disabled={hasUnassignedWages || createDefaultsMutation.isPending}
        >
          Income looks right →
        </Button>
      }
    >
      <WebHead>
        <title>Step 4: Your Income – Per Diem</title>
      </WebHead>
      {incomeQ.isLoading ? (
        <LoadingSpinner fullScreen={false} />
      ) : (
        <View className="gap-4">
          <FormErrorBanner error={error} onDismiss={() => setError(null)} />
          {autoDetectMessage && (
            <View className="rounded-xl bg-blue-50 dark:bg-blue-900/30 px-4 py-3">
              <Text className="text-sm text-blue-700 dark:text-blue-300">
                {autoDetectMessage}
              </Text>
            </View>
          )}
          {hasUnassignedWages && (
            <View className="rounded-xl bg-amber-50 dark:bg-amber-900/30 px-4 py-3">
              <Text className="text-sm text-amber-800 dark:text-amber-300">
                Assign each wage income stream to a person before continuing.
                Person-level FIRE target years only stop wages that are linked
                to the right household member.
              </Text>
            </View>
          )}

          {incomeStreams.length === 0 && createDefaultsMutation.isPending && (
            <Card>
              <Text className="text-sm text-gray-600 dark:text-gray-400">
                Looking for recurring income deposits…
              </Text>
            </Card>
          )}

          {incomeStreams.length === 0 &&
            !createDefaultsMutation.isPending &&
            !autoDetectMessage && (
              <Card>
                <Text className="text-sm text-gray-600 dark:text-gray-400">
                  {hasTransactionReviewSource && autoDetectAttempted
                    ? "No recurring income deposits were found. Add income manually below."
                    : "No income streams found yet. Add income manually below."}
                </Text>
              </Card>
            )}

          <IncomeStreamForm
            profiles={profiles}
            onAddIncome={handleAddManual}
            loading={createMutation.isPending}
            addButtonLabel="+ Add income manually"
            cardTitle="Add Income Stream"
          />

          {incomeStreams.length > 0 && (
            <Card>
              <SectionHeader title={`Income Streams (${incomeStreams.length})`} />
              <IncomeStreamList
                streams={incomeStreams}
                profiles={profiles}
                onMutated={handleIncomeMutated}
              />
            </Card>
          )}

          {profilesQ.data && !hasSocialSecurityIncome && (
            <SocialSecurityEstimator
              profiles={profilesQ.data ?? []}
              incomeStreams={incomeStreams}
              mode="auto"
              optedOut={socialSecurityOptedOut}
              onOptOut={() => setSocialSecurityOptedOut(true)}
              onUndoOptOut={() => setSocialSecurityOptedOut(false)}
            />
          )}
        </View>
      )}
    </OnboardingShell>
  );
}
